import {
  AuthGuard,
  CmsConfig,
  ParamsMapping,
  RoutingConfig
} from "@spartacus/core";
import { AdminGuard } from "@spartacus/organization/administration/core";
import { TableConfig } from "@spartacus/storefront";
import {
  AssignCellComponent,
  ItemService,
  ListService,
  OrganizationTableType,
  PermissionDetailsCellComponent,
  UserApproverListComponent,
  UserAssignedApproverListComponent,
  UserAssignedPermissionListComponent,
  UserAssignedUserGroupListComponent,
  UserChangePasswordFormComponent,
  UserDetailsCellComponent,
  UserGroupDetailsCellComponent,
  UserItemService,
  UserPermissionListComponent,
  UserUserGroupListComponent
} from "@spartacus/organization/administration/components";
import { UigUserListComponent } from "./uig-user-list/uig-user-list.component";
import {
  MAX_OCC_INTEGER_VALUE,
  ROUTE_PARAMS
} from "../uig-organization.model";
import { UigStatusCellComponent } from "../shared/components/table/uig-status-cell/uig-status-cell.component";
import { UigUserDetailsComponent } from "./uig-user-details/uig-user-details.component";
import { UigUserListService } from "../services/uig-user-list/uig-user-list.service";
import { UigUserFormComponent } from "./uig-user-form/uig-user-form.component";
import { UigListService } from '../services/uig-list.service';
import { UigCellComponent } from '../uig-branches/uig-cell/uig-cell.component';
import { UigUserChangePasswordFormComponent } from '../shared/components/detail/uig-user-change-password-form/uig-user-change-password-form.component';

const listPath = `organization/users/:${ROUTE_PARAMS.userCode}`;
const paramsMapping: ParamsMapping = {
  userCode: 'customerId',
};

export const uigUserRoutingConfig: RoutingConfig = {
  routing: {
    routes: {
      orgUser: {
        paths: ['organization/users'],
      },
      orgUserCreate: {
        paths: ['organization/users/create'],
      },
      orgUserDetails: {
        paths: [listPath],
        paramsMapping,
      },
      orgUserEdit: {
        paths: [`${listPath}/edit`],
        paramsMapping,
      },
      orgUserChangePassword: {
        paths: [`${listPath}/change-password`],
        paramsMapping,
      },
      orgUserApprovers: {
        paths: [`${listPath}/approvers`],
        paramsMapping,
      },
      orgUserAssignApprovers: {
        paths: [`${listPath}/approvers/assign`],
        paramsMapping,
      },
      orgUserPermissions: {
        paths: [`${listPath}/purchase-limits`],
        paramsMapping,
      },
      orgUserAssignPermissions: {
        paths: [`${listPath}/purchase-limits/assign`],
        paramsMapping,
      },
      orgUserUserGroups: {
        paths: [`${listPath}/user-groups`],
        paramsMapping,
      },
      orgUserAssignUserGroups: {
        paths: [`${listPath}/user-groups/assign`],
        paramsMapping,
      },
    },
  },
};

export const uigUserCmsConfig: CmsConfig = {
  cmsComponents: {
    ManageUsersListComponent: {
      component: UigUserListComponent,
      providers: [
        {
          provide: UigListService,
          useExisting: UigUserListService,
        },
        {
          provide: ItemService,
          useExisting: UserItemService,
        },
        {
          provide: ListService,
          useExisting: UigUserListService,
        }
      ],
      childRoutes: {
        parent: {
          data: {
            cxPageMeta: {
              breadcrumb: 'orgUser.breadcrumbs.list',
            },
          },
        },
        children: [
          {
            path: 'create',
            component: UigUserFormComponent,
          },
          {
            path: `:${ROUTE_PARAMS.userCode}`,
            component: UigUserDetailsComponent,
            data: {
              cxPageMeta: { breadcrumb: 'orgUser.breadcrumbs.details' },
            },
            children: [
              {
                path: `edit`,
                component: UigUserFormComponent,
              },
              {
                path: `change-password`,
                component: UigUserChangePasswordFormComponent,
              },
              {
                path: 'user-groups',
                data: {
                  cxPageMeta: { breadcrumb: 'orgUser.breadcrumbs.userGroups' },
                },
                children: [
                  {
                    path: '',
                    component: UserAssignedUserGroupListComponent,
                  },
                  {
                    path: 'assign',
                    component: UserUserGroupListComponent,
                  },
                ],
              },
              {
                path: 'approvers',
                data: {
                  cxPageMeta: { breadcrumb: 'orgUser.breadcrumbs.approvers' },
                },
                children: [
                  {
                    path: '',
                    component: UserAssignedApproverListComponent,
                  },
                  {
                    path: 'assign',
                    component: UserApproverListComponent,
                  },
                ],
              },
              {
                path: 'purchase-limits',
                data: {
                  cxPageMeta: { breadcrumb: 'orgUser.breadcrumbs.permissions' },
                },
                children: [
                  {
                    path: '',
                    component: UserAssignedPermissionListComponent,
                  },
                  {
                    path: 'assign',
                    component: UserPermissionListComponent,
                  },
                ],
              },
            ],
          },
        ],
      },
      guards: [AuthGuard, AdminGuard],
    },
  },
};

export function uigUserTableConfigFactory(): TableConfig {
  return uigUserTableConfig;
}

const actions = {
  dataComponent: AssignCellComponent,
};

const pagination = {
  pageSize: MAX_OCC_INTEGER_VALUE,
};

export const uigUserTableConfig: TableConfig = {
  table: {
    [OrganizationTableType.USER]: {
      cells: [
        'name',
        'uid',
        'email',
        'active'
      ],
      options: {
        cells: {
          name: {
            dataComponent: UigCellComponent,
          },
          uid: {
            dataComponent: UigCellComponent,
          },
          email: {
            dataComponent: UigCellComponent,
          },
          active: {
            dataComponent: UigStatusCellComponent,
          },
        },
      },
    },
    [OrganizationTableType.USER_APPROVERS]: {
      cells: ['name', 'actions'],
      options: {
        cells: {
          name: {
            dataComponent: UserDetailsCellComponent,
          },
          actions,
        },
      },
    },
    [OrganizationTableType.USER_ASSIGNED_APPROVERS]: {
      cells: ['name', 'actions'],
      options: {
        cells: {
          name: {
            dataComponent: UserDetailsCellComponent,
          },
          actions,
        },
        pagination,
      },
    },
    [OrganizationTableType.USER_USER_GROUPS]: {
      cells: ['name', 'actions'],
      options: {
        cells: {
          name: {
            dataComponent: UserGroupDetailsCellComponent,
          },
          actions,
        },
      },
    },
    [OrganizationTableType.USER_ASSIGNED_USER_GROUPS]: {
      cells: ['name', 'actions'],
      options: {
        cells: {
          name: {
            dataComponent: UserGroupDetailsCellComponent,
          },
          actions,
        },
        pagination,
      },
    },
    [OrganizationTableType.USER_PERMISSIONS]: {
      cells: ['code', 'actions'],
      options: {
        cells: {
          code: {
            dataComponent: PermissionDetailsCellComponent,
          },
          actions,
        },
      },
    },
    [OrganizationTableType.USER_ASSIGNED_PERMISSIONS]: {
      cells: ['code', 'actions'],
      options: {
        cells: {
          code: {
            dataComponent: PermissionDetailsCellComponent,
          },
          actions,
        },
        pagination,
      },
    },
  },
};
